import { createFileRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'
import { isAxiosError } from 'axios'
import { ResumeCheckResultResultEnum, type ResumeImpersonatedUser } from '@portal/pkg-api/gen/auth'
import { UserInfoQueries } from '@shared/data'
import { DEFAULT_REDIRECT_TARGET, sanitizeRedirectTarget } from '@auth/redirectTarget'
import {
  deleteIdleLogoutEntry,
  deleteLegacyIdleLogoutKeys,
  type IdleLogoutEntry,
  readIdleLogoutEntry,
} from '@auth/data/idleLogout'
import { redeemResume } from '@auth/resume/data'
import { isTransientFailure, isUnauthorized } from '@auth/resume/requestFailure'
import ResumePage from '@auth/resume/ResumePage'

/**
 * Where a logged-out tab waits. Once the user is logged in again, it sends the tab to its saved
 * page, if the server agrees the place belongs to this user, and otherwise home. A place saved
 * while impersonating first asks the admin whether to resume as that user. If the request fails
 * without the server deciding, the place is kept and the page offers to try again.
 */
export const Route = createFileRoute('/_login/resume')({
  component: RouteComponent,
  validateSearch: z.object({
    // Why there's nothing to resume. Only these values; anything else is ignored.
    reason: z.enum(['other-tab', 'ended']).optional().catch(undefined),
  }),
  beforeLoad: async ({ context: { queryClient } }): Promise<ResumeRouteContext> => {
    deleteLegacyIdleLogoutKeys()
    const entry = readIdleLogoutEntry()
    try {
      await queryClient.ensureQueryData(UserInfoQueries.userInfo())
    } catch (error) {
      // Only a 401 means logged out; let anything else reach the route error handling.
      if (!isAxiosError(error) || error.response?.status !== 401) {
        throw error
      }
      return { entry, choice: undefined }
    }
    const outcome = await redeem(entry)
    switch (outcome.kind) {
      case 'choose':
        // The page asks. The entry stays, so the tab can redeem again once the choice is made.
        return { entry, choice: outcome.user, loggedIn: true }
      case 'loggedOut':
        return { entry, choice: undefined }
      case 'failed':
        // Nothing was decided, so the entry stays and the page offers to try again.
        return { entry, choice: undefined, failed: true, loggedIn: true }
      case 'go':
        // A place is restored at most once.
        deleteIdleLogoutEntry()
        throw redirect({ href: outcome.target, replace: true })
    }
  },
})

interface ResumeRouteContext {
  entry: IdleLogoutEntry | undefined
  choice: ResumeImpersonatedUser | undefined
  failed?: boolean
  /** The user is confirmed, but the tab stays on this page. */
  loggedIn?: boolean
}

function RouteComponent() {
  const { entry, choice, failed, loggedIn } = Route.useRouteContext()
  const { reason } = Route.useSearch()
  return (
    <ResumePage entry={entry} reason={reason} choice={choice} failed={failed} loggedIn={loggedIn} />
  )
}

type RedeemOutcome =
  /** The saved page, or home when the server decided there's none. */
  | { kind: 'go'; target: string }
  /** The admin chooses whether to resume as the user they were impersonating. */
  | { kind: 'choose'; user: ResumeImpersonatedUser }
  /** No one is logged in after all. */
  | { kind: 'loggedOut' }
  /** The request failed without the server deciding anything. */
  | { kind: 'failed' }

/**
 * Where the tab goes: its saved page, if the server returns it for this session's user, and
 * otherwise home. The server decrypts and checks the address; it's checked again here before
 * anything navigates to it. Or, for a place saved while impersonating, the user to ask about.
 *
 * Only the server's answer sends the tab home. A request that failed without one keeps the place.
 */
async function redeem(entry: IdleLogoutEntry | undefined): Promise<RedeemOutcome> {
  if (!entry?.handle) {
    return { kind: 'go', target: DEFAULT_REDIRECT_TARGET }
  }
  let redemption
  try {
    redemption = await redeemResume({ handle: entry.handle, iv: entry.iv, ct: entry.ct })
  } catch (error) {
    if (isUnauthorized(error)) {
      return { kind: 'loggedOut' }
    }
    if (isTransientFailure(error)) {
      return { kind: 'failed' }
    }
    // Refused, for example a malformed entry: there's no place to restore.
    return { kind: 'go', target: DEFAULT_REDIRECT_TARGET }
  }
  const { result, url, impersonatedUser } = redemption
  if (result === ResumeCheckResultResultEnum.restore) {
    return { kind: 'go', target: sanitizeRedirectTarget(url) }
  }
  if (result === ResumeCheckResultResultEnum.impersonation_choice && impersonatedUser) {
    return { kind: 'choose', user: impersonatedUser }
  }
  // Every other result is the server deciding there's no place: home.
  return { kind: 'go', target: DEFAULT_REDIRECT_TARGET }
}
